import { Component, ErrorHandler, Inject, ChangeDetectionStrategy, signal } from '@angular/core'
import { FieldTree, form } from '@angular/forms/signals'
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog'
import { MatSnackBar } from '@angular/material/snack-bar'

import { NumberDate } from '@lemon/core/model/common'
import { DateService } from '@lemon/core/service/date.service'

import { LadataanService } from 'app/_jaettu-angular/service/ladataan.service'
import { KirjanpitajanTiimi, KirjanpitajanTiimikokonaisuus } from 'app/_jaettu-lemonator/model/kirjanpitaja'

import { ALKU_AIKOJEN_ALUSTA, TiimienHallintaService, TiimiRooli } from './tiimien-hallinta.service'

export interface MuokkaaJasenyysDialogData {
  // Henkilön nimi näytetään dialogin yläosassa
  nimi: string
  // Onko jäsen tiimissä vai tiimikokonaisuudessa
  kohde: { tyyppi: 'tiimi'; tiimiAvain: KirjanpitajanTiimi['avain']; rooli: TiimiRooli } | { tyyppi: 'kokonaisuus'; kokonaisuusAvain: KirjanpitajanTiimikokonaisuus['avain'] }
  kirjanpitajaAvain: string
  // Jakson nykyinen alkupäivä yksilöi useista jaksoista oikean
  jaksonAlku: NumberDate
  nykyinenLoppu: NumberDate | null
  nykyinenPrimary: boolean
}

interface JasenyysLomake {
  alku: Date | null
  loppu: Date | null
  primary: boolean
}

@Component({
  templateUrl: './muokkaa-jasenyys.dialog.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: false,
})
export class MuokkaaJasenyysDialog {
  lomake: FieldTree<JasenyysLomake>

  virhe: string = null

  constructor(
    @Inject(MAT_DIALOG_DATA) public data: MuokkaaJasenyysDialogData,
    private _dialogRef: MatDialogRef<MuokkaaJasenyysDialog>,
    private _ladataanService: LadataanService,
    private _hallintaService: TiimienHallintaService,
    private _dateService: DateService,
    private _snackbar: MatSnackBar,
    private _errorHandler: ErrorHandler,
  ) {
    const alkuEsitayte = data.jaksonAlku && data.jaksonAlku !== ALKU_AIKOJEN_ALUSTA ? this._dateService.numberToDate(data.jaksonAlku) : null
    const loppuEsitayte = data.nykyinenLoppu ? this._dateService.numberToDate(data.nykyinenLoppu) : null
    this.lomake = form(signal<JasenyysLomake>({ alku: alkuEsitayte, loppu: loppuEsitayte, primary: !!data.nykyinenPrimary }))
  }

  peruuta() {
    this._dialogRef.close()
  }

  async tallenna() {
    this.virhe = null
    const arvot = this.lomake().value()
    const alku: NumberDate = arvot.alku ? this._dateService.dateToNumber(arvot.alku) : ALKU_AIKOJEN_ALUSTA
    const loppu: NumberDate | null = arvot.loppu ? this._dateService.dateToNumber(arvot.loppu) : null
    if (loppu !== null && loppu < alku) {
      this.virhe = 'Päättymispäivä ei voi olla ennen alkupäivää.'
      return
    }
    try {
      this._ladataanService.aloitaLataaminen()
      const uusi = { alku, loppu, primary: !!arvot.primary }
      if (this.data.kohde.tyyppi === 'tiimi') {
        await this._hallintaService.muokkaaTiiminJaksoa(this.data.kohde.tiimiAvain, this.data.kohde.rooli, this.data.kirjanpitajaAvain, this.data.jaksonAlku, uusi)
      } else {
        await this._hallintaService.muokkaaKokonaisuudenJaksoa(this.data.kohde.kokonaisuusAvain, this.data.kirjanpitajaAvain, this.data.jaksonAlku, uusi)
      }
      this._snackbar.open('Jäsenyysjakso tallennettu', 'OK', { duration: 5000 })
      this._dialogRef.close(true)
    } catch (err) {
      this._errorHandler.handleError(err)
      this._snackbar.open('Tallennus epäonnistui', 'OK', { duration: 5000 })
    } finally {
      this._ladataanService.lopetaLataaminen()
    }
  }
}
